import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';

export default function BillPrint() {
  const [searchParams] = useSearchParams();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ids = searchParams.get('ids');
    if (!ids) return;
    const orderIds = ids.split(',').map(Number).filter(Boolean);
    api.post('/orders/bills/multi', { order_ids: orderIds })
      .then(r => { setBills(r.data.bills); setLoading(false); })
      .catch(() => setLoading(false));
  }, [searchParams]);

  useEffect(() => {
    if (!loading && bills.length > 0) {
      setTimeout(() => window.print(), 500);
    }
  }, [loading, bills]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', fontFamily: 'sans-serif' }}>Preparing bills...</div>;
  if (!bills.length) return <div style={{ padding: 40, textAlign: 'center', fontFamily: 'sans-serif' }}>No bills found.</div>;

  return (
    <>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Hanuman', 'DM Sans', sans-serif; background: #fff; }
        .bill {
          width: 148mm;
          min-height: 105mm;
          max-height: 105mm;
          padding: 8mm 10mm;
          page-break-after: always;
          overflow: hidden;
          border: 1px solid #eee;
          margin: 0 auto;
        }
        .bill:last-child { page-break-after: avoid; }
        .header { text-align: center; margin-bottom: 6px; }
        .biz-name { font-size: 16px; font-weight: 700; }
        .biz-sub { font-size: 10px; color: #666; margin-top: 1px; }
        .divider { border: none; border-top: 1px dashed #ccc; margin: 5px 0; }
        .order-meta { font-size: 10px; color: #666; display: flex; justify-content: space-between; margin-bottom: 4px; }
        .customer-box { background: #f9f9f9; border-radius: 4px; padding: 5px 8px; margin-bottom: 6px; }
        .customer-name { font-size: 13px; font-weight: 700; }
        .customer-detail { font-size: 10px; color: #555; margin-top: 1px; }
        .items-table { width: 100%; border-collapse: collapse; margin-bottom: 5px; }
        .items-table th { font-size: 9px; color: #888; text-align: left; padding: 2px 0; border-bottom: 1px solid #eee; text-transform: uppercase; }
        .items-table td { font-size: 11px; padding: 3px 0; border-bottom: 1px solid #f5f5f5; }
        .items-table td:last-child { text-align: right; font-weight: 600; }
        .totals { margin-top: 4px; }
        .total-row { display: flex; justify-content: space-between; font-size: 11px; color: #555; margin-bottom: 2px; }
        .grand-total { display: flex; justify-content: space-between; font-size: 15px; font-weight: 700; border-top: 1.5px solid #333; padding-top: 4px; margin-top: 4px; }
        .footer { text-align: center; font-size: 9px; color: #999; margin-top: 6px; }
        .loc-badge { display: inline-block; font-size: 9px; padding: 1px 6px; border-radius: 8px; font-weight: 600; margin-left: 4px; }
        .loc-pp { background: #dbeafe; color: #1d4ed8; }
        .loc-prov { background: #fef3c7; color: #92400e; }
        @media print {
          @page { size: A5 landscape; margin: 0; }
          body { margin: 0; }
          .no-print { display: none; }
        }
      `}</style>

      {/* Print button - hidden on print */}
      <div className="no-print" style={{ padding: '16px 24px', background: '#1a1d27', display: 'flex', alignItems: 'center', gap: 16 }}>
        <span style={{ color: '#e8e6f0', fontWeight: 600 }}>📄 {bills.length} bill{bills.length > 1 ? 's' : ''} ready</span>
        <button onClick={() => window.print()} style={{ background: '#7c6af5', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>🖨 Print Now</button>
        <button onClick={() => window.close()} style={{ background: '#1e2130', color: '#6b6d85', border: '1px solid #2a2d3e', borderRadius: 8, padding: '8px 16px', fontSize: 14, cursor: 'pointer' }}>✕ Close</button>
      </div>

      {bills.map((bill, idx) => (
        <div key={idx} className="bill">
          <div className="header">
            <div className="biz-name">💎 {bill.business_name}</div>
            {bill.business_phone && <div className="biz-sub">📞 {bill.business_phone}</div>}
          </div>
          <hr className="divider" />
          <div className="order-meta">
            <span>📋 {bill.order_number}</span>
            <span>📅 {bill.date}</span>
            <span>
              {bill.delivery_category === 'phnom_penh'
                ? <span className="loc-badge loc-pp">PP</span>
                : bill.delivery_category === 'province'
                ? <span className="loc-badge loc-prov">Province</span>
                : <span className="loc-badge" style={{ background: '#f3f4f6', color: '#6b7280' }}>No Contact</span>
              }
            </span>
          </div>
          <div className="customer-box">
            <div className="customer-name">{bill.customer.name}</div>
            {bill.customer.phone && <div className="customer-detail">📞 {bill.customer.phone}</div>}
            {bill.customer.address && <div className="customer-detail">📍 {bill.customer.address}</div>}
          </div>
          <table className="items-table">
            <thead>
              <tr>
                <th>Item</th>
                <th style={{ textAlign: 'center' }}>Qty</th>
                <th style={{ textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {bill.items.map((item, i) => (
                <tr key={i}>
                  <td>{item.name}{item.live_code ? ` (${item.live_code})` : ''}</td>
                  <td style={{ textAlign: 'center' }}>{item.quantity}</td>
                  <td>${item.subtotal.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="totals">
            {bill.delivery_fee > 0 && (
              <div className="total-row"><span>Delivery</span><span>${bill.delivery_fee.toFixed(2)}</span></div>
            )}
            {bill.discount > 0 && (
              <div className="total-row"><span>Discount</span><span>-${bill.discount.toFixed(2)}</span></div>
            )}
            <div className="grand-total">
              <span>Total</span>
              <span>${bill.total.toFixed(2)}</span>
            </div>
          </div>
          {bill.staff_note && <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>Note: {bill.staff_note}</div>}
          <div className="footer">Thank you! 🙏 {bill.business_name}</div>
        </div>
      ))}
    </>
  );
}
